import { useTheme } from "../context/ThemeContext";
import { useState, useEffect } from "react";
import { getTransactions, getBudgetSummary, getCategories, getIncome, getHousehold, expandSplits } from "../api/client";

const fmt = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (a, b) => b === 0 ? 0 : Math.min(100, Math.round((a / b) * 100));

const ProgressBar = ({ value, max, color, height = 6 }) => {
  const { COLORS } = useTheme();
  const p = pct(value, max);
  const barColor = color || (p > 90 ? COLORS.red : p > 70 ? COLORS.amber : COLORS.green);
  return (
    <div style={{ height, background: COLORS.border, borderRadius: 99, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(100,p)}%`, height: "100%", background: barColor, borderRadius: 99, transition: "width 0.4s ease" }} />
    </div>
  );
};

const getPeriodStart = (monthStart = 26) => {
  const today = new Date();
  if (today.getDate() >= monthStart) return new Date(today.getFullYear(), today.getMonth(), monthStart);
  return new Date(today.getFullYear(), today.getMonth() - 1, monthStart);
};

// Mirror of the server's cadenceHits (services/projection.js): does a
// quarterly/annual category land in this budget period? Monthly always does.
// Its payment month is anchor_month; the due day decides whether that calendar
// month sits in this period's opening month or the next (26th-anchored periods).
const cadenceHitsPeriod = (c, periodStartDate, monthStart) => {
  const cad = c.cadence || 'monthly';
  if (cad === 'monthly') return true;
  const anchor = Number(c.anchor_month);
  if (!(anchor >= 1 && anchor <= 12)) return true; // missing anchor: stay visible
  const effDay = c.due_day != null ? Number(c.due_day) : 15;
  const m0 = effDay >= monthStart ? periodStartDate.getMonth() : periodStartDate.getMonth() + 1;
  const cal = (m0 % 12) + 1;
  if (cad === 'annual') return cal === anchor;
  return (((cal - anchor) % 3) + 3) % 3 === 0;
};

const getPeriodEnd = (monthStart = 26) => {
  const start = getPeriodStart(monthStart);
  return new Date(start.getFullYear(), start.getMonth() + 1, monthStart - 1);
};

const getWeeks = (monthStart = 26) => {
  const periodStart = getPeriodStart(monthStart);
  const periodEnd   = getPeriodEnd(monthStart);
  const firstThursday = new Date(periodStart);
  while (firstThursday.getDay() !== 4) firstThursday.setDate(firstThursday.getDate() + 1);
  const weeks = [];
  if (firstThursday > periodStart) {
    const partialEnd = new Date(firstThursday);
    partialEnd.setDate(firstThursday.getDate() - 1);
    weeks.push({ label: "Week 1", start: periodStart.toISOString().split("T")[0], end: partialEnd.toISOString().split("T")[0], startDate: new Date(periodStart), endDate: new Date(partialEnd), startLabel: periodStart.toLocaleDateString("en-US", { month: "short", day: "numeric" }), endLabel: partialEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" }) });
  }
  let weekStart = new Date(firstThursday);
  while (weekStart <= periodEnd) {
    const weekEnd    = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const clampedEnd = weekEnd > periodEnd ? new Date(periodEnd) : weekEnd;
    weeks.push({ label: `Week ${weeks.length + 1}`, start: weekStart.toISOString().split("T")[0], end: clampedEnd.toISOString().split("T")[0], startDate: new Date(weekStart), endDate: new Date(clampedEnd), startLabel: weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" }), endLabel: clampedEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" }) });
    weekStart = new Date(clampedEnd);
    weekStart.setDate(clampedEnd.getDate() + 1);
  }
  return weeks;
};

const getCurrentWeekIndex = (weeks) => {
  const today = new Date().toISOString().split("T")[0];
  for (let i = 0; i < weeks.length; i++) {
    if (today >= weeks[i].start && today <= weeks[i].end) return i;
  }
  return weeks.length - 1;
};

const dayOfMonthInWeek = (day, startDate, endDate) => {
  const d = new Date(startDate);
  while (d <= endDate) {
    if (d.getDate() === day) return true;
    d.setDate(d.getDate() + 1);
  }
  return false;
};

const DAYS = { Sunday:0, Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6 };

const weekdayInWeek = (weekdayName, startDate, endDate) => {
  const target = DAYS[weekdayName];
  if (target === undefined) return false;
  const d = new Date(startDate);
  while (d <= endDate) {
    if (d.getDay() === target) return true;
    d.setDate(d.getDate() + 1);
  }
  return false;
};

const getExpectedIncome = (incomeSources, weekStartDate, weekEndDate) => {
  const items = [];
  incomeSources.forEach(src => {
    const amt = parseFloat(src.amount || 0);
    if (src.schedule_type === "weekly") {
      if (weekdayInWeek(src.week_day, weekStartDate, weekEndDate))
        items.push({ id: src.id, name: src.name, amount: amt });
    } else if (src.schedule_type === "biweekly") {
      if (weekdayInWeek(src.week_day, weekStartDate, weekEndDate)) {
        const target = DAYS[src.week_day];
        const d = new Date(weekStartDate);
        while (d.getDay() !== target) d.setDate(d.getDate() + 1);
        const anchor = new Date(2020, 0, 2);
        const diffWeeks = Math.round((d - anchor) / (7 * 24 * 60 * 60 * 1000));
        if (diffWeeks % 2 === 0) items.push({ id: src.id, name: src.name, amount: amt });
      }
    } else if (src.schedule_type === "monthly") {
      if (src.month_day && dayOfMonthInWeek(parseInt(src.month_day), weekStartDate, weekEndDate))
        items.push({ id: src.id, name: src.name, amount: amt });
    } else if (src.schedule_type === "twiceMonthly") {
      let added = false;
      if (src.month_day_1 && dayOfMonthInWeek(parseInt(src.month_day_1), weekStartDate, weekEndDate)) {
        items.push({ id: src.id, name: src.name, amount: amt }); added = true;
      }
      if (!added && src.month_day_2 && dayOfMonthInWeek(parseInt(src.month_day_2), weekStartDate, weekEndDate))
        items.push({ id: src.id, name: src.name, amount: amt });
    }
  });
  return items;
};

const Weekly = () => {
  const { COLORS } = useTheme();
  const [transactions,  setTransactions]  = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [household,     setHousehold]     = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [selectedWeek,  setSelectedWeek]  = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [txRes, catRes, incRes, hhRes] = await Promise.all([
          getTransactions(), getCategories(), getIncome(), getHousehold()
        ]);
        const txData  = txRes.data;
        const catData = catRes.data;
        const incData = incRes.data;
        const hhData  = hhRes.data;
        setTransactions(Array.isArray(txData) ? txData : txData?.transactions || txData?.data || []);
        setCategories(Array.isArray(catData) ? catData : catData?.data || []);
        setIncomeSources(Array.isArray(incData) ? incData : incData?.data || []);
        const hh = Array.isArray(hhData) ? null : hhData?.data || hhData || null;
        setHousehold(hh);
        const ms = hh?.month_start || 26;
        setSelectedWeek(getCurrentWeekIndex(getWeeks(ms)));
      } catch(err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div style={{ padding: 24, color: COLORS.textMuted, textAlign: "center", paddingTop: 80 }}>Loading...</div>
  );

  const monthStart  = household?.month_start || 26;
  const weeks       = getWeeks(monthStart);
  const week        = weeks[selectedWeek] || weeks[weeks.length - 1];
  const periodStart = getPeriodStart(monthStart).toISOString().split("T")[0];
  const periodEnd   = getPeriodEnd(monthStart).toISOString().split("T")[0];
  const categoryMap = Object.fromEntries(categories.map(c => [c.id, c]));
  // Only count categories that are turned on (is_active). Inactive ones are kept in
  // the raw list for Settings, but must not appear in budgets/bills here.
  // Quarterly/annual categories count only in the period they actually hit.
  const activeCategories = categories.filter(c =>
    c.is_active !== false && cadenceHitsPeriod(c, getPeriodStart(monthStart), monthStart));

  // Expand split transactions into per-category rows so category spend counts the
  // splits, not the parent's single category (see expandSplits in api/client).
  const expandedTxns = expandSplits(transactions);
  const weekTxns     = expandedTxns.filter(t => t.date >= week.start && t.date <= week.end);
  const weekExpenses = weekTxns.filter(t => t.type === "expense");

  // Only look back 2 days if a monthly payday falls on a weekend
  const hasWeekendPayday = incomeSources.some(src => {
    const days = [];
    if (src.schedule_type === "monthly" && src.month_day) days.push(parseInt(src.month_day));
    if (src.schedule_type === "twiceMonthly") {
      if (src.month_day_1) days.push(parseInt(src.month_day_1));
      if (src.month_day_2) days.push(parseInt(src.month_day_2));
    }
    return days.some(day => {
      const d = new Date(week.startDate);
      const end = new Date(week.endDate);
      const lookback = new Date(d); lookback.setDate(d.getDate() - 2);
      let cur = new Date(lookback);
      while (cur <= end) {
        if (cur.getDate() === day) { const wd = cur.getDay(); return wd === 0 || wd === 6; }
        cur.setDate(cur.getDate() + 1);
      }
      return false;
    });
  });

  const weekIncomeStartStr = (() => {
    if (!hasWeekendPayday) return week.start;
    const d = new Date(week.startDate); d.setDate(d.getDate() - 2);
    return d.toISOString().split("T")[0];
  })();

  const weekIncome = transactions.filter(t =>
    t.type === "income" && t.date >= weekIncomeStartStr && t.date <= week.end
  );
  const periodExpenses = expandedTxns.filter(t => t.type === "expense" && t.date >= periodStart && t.date <= periodEnd);

  const actualIncome = weekIncome.filter(t => t.income_source_id != null).reduce((s,t) => s + parseFloat(t.amount), 0);
  const expectedIncomeItems = getExpectedIncome(incomeSources, week.startDate, week.endDate);
  const expectedIncomeTotal = expectedIncomeItems.reduce((s,i) => s + i.amount, 0);

  const fixedDueItems = activeCategories
    .filter(c => c.type === "fixed" && c.due_day && dayOfMonthInWeek(parseInt(c.due_day), week.startDate, week.endDate))
    .map(cat => {
      const paidAmt = periodExpenses.filter(t => t.category_id === cat.id).reduce((s,t) => s + parseFloat(t.amount), 0);
      return { name: cat.name, amount: parseFloat(cat.amount||0), id: cat.id, due_day: cat.due_day, paidAmt, paid: paidAmt >= parseFloat(cat.amount||0) * 0.9 };
    });
  const fixedDueTotal = fixedDueItems.reduce((s,i) => s + i.amount, 0);
  const fixedPaidTotal = fixedDueItems.reduce((s,i) => s + i.paidAmt, 0);

  // Variable — only show if spending occurred this week
  const variableTrackers = activeCategories
    .filter(c => (c.type === "variable" || c.type === "misc"))
    .map(cat => {
      const monthlyBudget = parseFloat(cat.amount||0);
      const weeklyTarget  = cat.type === 'misc' ? 0 : monthlyBudget / 4;
      const spentMonth    = periodExpenses.filter(t => t.category_id === cat.id).reduce((s,t) => s + parseFloat(t.amount), 0);
      const spentWeek     = weekExpenses.filter(t => t.category_id === cat.id).reduce((s,t) => s + parseFloat(t.amount), 0);
      const remaining     = Math.max(0, monthlyBudget - spentMonth);
      return { cat, monthlyBudget, weeklyTarget, spentMonth, spentWeek, remaining };
    })
    .filter(v => v.spentWeek > 0);

  return (
    <div style={{ padding: "0 0 100px", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ padding: "20px 16px 10px" }}>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Weekly</h2>
        <p style={{ color: COLORS.textMuted, fontSize: 13, margin: "4px 0 0" }}>{week.startLabel} — {week.endLabel}</p>
      </div>

      {/* Week selector */}
      <div style={{ padding: "0 12px 14px", display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none" }}>
        {weeks.map((w, i) => (
          <button key={i} onClick={() => setSelectedWeek(i)} style={{ flexShrink: 0, padding: "10px 14px", borderRadius: 12, border: `1px solid ${selectedWeek === i ? COLORS.accent : COLORS.border}`, background: selectedWeek === i ? COLORS.accentGlow : COLORS.surface, color: selectedWeek === i ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 12 }}>
            {w.label}
          </button>
        ))}
      </div>

      <div style={{ padding: "0 12px", display: "flex", flexDirection: "column", gap: 10 }}>

        {/* Income */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12 }}>Income</div>
          <div style={{ display: "flex", gap: 10, marginBottom: expectedIncomeItems.length > 0 ? 14 : 0 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 4 }}>Expected</div>
              <div style={{ color: COLORS.text, fontFamily: "monospace", fontWeight: 700, fontSize: 22 }}>
                {expectedIncomeTotal > 0 ? `+${fmt(expectedIncomeTotal)}` : "—"}
              </div>
            </div>
            <div style={{ flex: 1, textAlign: "right" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 4 }}>Received</div>
              <div style={{ color: actualIncome > 0 ? COLORS.green : COLORS.textMuted, fontFamily: "monospace", fontWeight: 700, fontSize: 22 }}>
                {actualIncome > 0 ? `+${fmt(actualIncome)}` : "—"}
              </div>
            </div>
          </div>
          {expectedIncomeItems.length > 0 && (
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Source</span>
                <div style={{ display: "flex", gap: 24 }}>
                  <span style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Expected</span>
                  <span style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Actual</span>
                </div>
              </div>
              {expectedIncomeItems.map((item, i) => {
                const actual = weekIncome.filter(t => t.income_source_id === item.id).reduce((s,t) => s + parseFloat(t.amount), 0);
                return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: COLORS.textMuted, fontSize: 13 }}>{item.name}</span>
                    <div style={{ display: "flex", gap: 24 }}>
                      <span style={{ color: COLORS.text, fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>+{fmt(item.amount)}</span>
                      <span style={{ color: actual > 0 ? COLORS.green : COLORS.textMuted, fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>{actual > 0 ? `+${fmt(actual)}` : "—"}</span>
                    </div>
                  </div>
                );
              })}
            </div>

          )}

          {/* Other income — unlinked income transactions this week */}
          {(() => {
            const linkedIds = new Set(expectedIncomeItems.map(item => item.id));
            const otherIncome = weekIncome.filter(t => !linkedIds.has(t.income_source_id) && parseFloat(t.amount) > 0);
            const otherTotal = otherIncome.reduce((s,t) => s + parseFloat(t.amount), 0);
            if (otherTotal === 0) return null;
            return (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                <span style={{ color: COLORS.textMuted, fontSize: 13 }}>Other Income</span>
                <div style={{ display: "flex", gap: 24 }}>
                  <span style={{ color: COLORS.textMuted, fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>—</span>
                  <span style={{ color: COLORS.green, fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>+{fmt(otherTotal)}</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Fixed Bills */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12 }}>Fixed Bills This Week</div>
          {fixedDueItems.length === 0 ? (
            <div style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "8px 0" }}>No bills due this week</div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 4 }}>Total Due</div>
                  <div style={{ color: COLORS.red, fontFamily: "monospace", fontWeight: 700, fontSize: 22 }}>-{fmt(fixedDueTotal)}</div>
                </div>
                <div style={{ flex: 1, textAlign: "right" }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 4 }}>Paid</div>
                  <div style={{ color: fixedPaidTotal > 0 ? COLORS.green : COLORS.textMuted, fontFamily: "monospace", fontWeight: 700, fontSize: 22 }}>
                    {fixedPaidTotal > 0 ? `-${fmt(fixedPaidTotal)}` : "—"}
                  </div>
                </div>
              </div>
              <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, maxHeight: 280, overflowY: "auto", scrollbarWidth: "none", display: "flex", flexDirection: "column", gap: 10 }}>
                {fixedDueItems.map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.paid ? COLORS.green : COLORS.amber, flexShrink: 0 }} />
                      <div>
                        <div style={{ color: item.paid ? COLORS.textMuted : COLORS.text, fontSize: 13, textDecoration: item.paid ? "line-through" : "none" }}>{item.name}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: 10 }}>Due day {item.due_day}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: item.paid ? COLORS.green : COLORS.red, fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>
                        {item.paid ? `-${fmt(item.paidAmt)}` : `-${fmt(item.amount)}`}
                      </div>
                      {item.paid && <div style={{ color: COLORS.green, fontSize: 10 }}>✓ paid</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Variable & Misc — only show if spending this week */}
        {variableTrackers.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 14 }}>Spending This Week</div>
            <div style={{ maxHeight: 400, overflowY: "auto", scrollbarWidth: "none", display: "flex", flexDirection: "column", gap: 16 }}>
              {variableTrackers.map(({ cat, monthlyBudget, weeklyTarget, spentMonth, spentWeek, remaining }, i, arr) => {
                const monthPct = pct(spentMonth, monthlyBudget);
                const isOver   = spentMonth > monthlyBudget;
                const barColor = monthPct > 90 ? COLORS.red : monthPct > 70 ? COLORS.amber : COLORS.green;
                return (
                  <div key={cat.id} style={{ paddingBottom: i < arr.length - 1 ? 16 : 0, borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                      <span style={{ color: COLORS.text, fontSize: 14, fontWeight: 700 }}>{cat.name}</span>
                      <span style={{ color: isOver ? COLORS.red : COLORS.textMuted, fontFamily: "monospace", fontSize: 12 }}>
                        {fmt(spentMonth)}{monthlyBudget > 0 ? ` / ${fmt(monthlyBudget)}` : ""}
                      </span>
                    </div>
                    {monthlyBudget > 0 && <ProgressBar value={spentMonth} max={monthlyBudget} color={barColor} height={5} />}
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                      <div>
                        <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 2 }}>This week</div>
                        <div style={{ color: weeklyTarget > 0 && spentWeek > weeklyTarget ? COLORS.amber : COLORS.text, fontFamily: "monospace", fontWeight: 700, fontSize: 13 }}>{fmt(spentWeek)}</div>
                      </div>
                      {monthlyBudget > 0 && (
                        <div style={{ textAlign: "right" }}>
                          <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 2 }}>Month left</div>
                          <div style={{ color: isOver ? COLORS.red : COLORS.green, fontFamily: "monospace", fontWeight: 700, fontSize: 13 }}>
                            {isOver ? `-${fmt(spentMonth - monthlyBudget)}` : fmt(remaining)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {variableTrackers.length === 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "24px 18px", textAlign: "center" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 13 }}>No variable or misc spending this week</div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Weekly;
